import React, { useState, useEffect } from "react";
import MasterTable from "../common/MasterTable";
import CustomAlert from "../../helpers/CustomAlert";
import { format, parse } from "date-fns";
import Loader from "../Loader/Loader";
import CustomerFilterModal from "./CustomerFilterModal";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import HeaderWithBreadcrumb from "../common/HeaderWithBreadcrumb";
import useExcelDownloader from "../../Hooks/useExcelDownloader";
import useTableExport from "../../Hooks/useTableExport";
import FilterBar from "../common/FilterBar";

const ENQUIRY_TYPE_OPTIONS = [
  { value: "FRANCHISE", label: "Franchise" },
  { value: "LMBP", label: "LMBP" },
  { value: "ENTERPRISE_PARTNER", label: "Enterprise Partner" },
  { value: "COUNTER_BOOKING", label: "Counter Booking" },
];

const defaultFilterData = {
  type: "",
  fullName: "",
  mobileNumber: "",
  emailId: "",
  createdDate: "",
  fromDate: "",
  toDate: "",
  search: "",
};

const CustomerEnquiries = ({ fetchData }) => {
  const { downloadExcel } = useExcelDownloader();
  const { exportAllData, exportLoading } = useTableExport(downloadExcel);
  const loggedInUserName = sessionStorage.getItem("username") || "admin";

  const [pageSize, setPageSize] = useState(10);
  const [noData, setNoData] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [pageDetails, setPageDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const openModalFilter = () => setIsModalOpenFilter(true);
  const closeModalFilter = () => setIsModalOpenFilter(false);

  const handleReset = () => {
    setFilterData(defaultFilterData);
    setCurrentPage(1);
  };

  const CustomerHeader = [
    "Type",
    "Customer Name",
    "City",
    "Mobile Number",
    "Email ID",
    "Enquiry Date and Time",
  ];

  const CustomerHeaderToKeyMap = {
    "Type": "type",
    "Customer Name": "fullName",
    "City": "city",
    "Mobile Number": "mobileNumber",
    "Email ID": "emailId",
    "Enquiry Date and Time": "createdAt",
  };

  useEffect(() => {
    fetchUserData(1, filterData);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const modifyStructure = (response, pageNo) => {
    return response?.map((user, index) => {
      const CreatedDateParsed = user?.createdAt
        ? parse(user.createdAt, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const formattedDateTime = CreatedDateParsed
        ? `${format(CreatedDateParsed, "dd-MM-yyyy")} ${format(
            CreatedDateParsed,
            "HH:mm:ss"
          )}`
        : "-";

      return {
        serialNumber: String(index + 1 + (pageNo - 1) * pageSize),
        type: user?.type || "-",
        fullName: user?.fullName || "-",
        city: user?.city || "-",
        mobileNumber: user?.mobileNumber || "-",
        emailId: user?.emailId || "-",
        createdAt: formattedDateTime
          ? [
              format(CreatedDateParsed, "dd-MM-yyyy"),
              <br key={index} />,
              format(CreatedDateParsed, "HH:mm:ss"),
            ]
          : ["-", <br key={index} />, "-"],
      };
    });
  };

  const fetchUserData = async (currentPage, filterData, size = pageSize) => {
    try {
      const requestBody = {
        username: loggedInUserName,
        ...filterData,
        fromDate: formatDate(filterData?.fromDate),
        toDate: formatDate(filterData?.toDate),
        // type: "ENTERPRISE_PARTNER",
        // "fullName": "rahul",
        // "mobileNumber": "9876543210",
        // "city": "Mumbai",
      };

      setLoading(true);

      const response = await axiosPostApi(
        `${apiList.FETCH_CUSTOMER_ENQUIRIES}?&pageSize=${size}&pageNo=${currentPage}`,
        requestBody
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data, currentPage);
        setLedgerData(modifiedData);
        setNoData(modifiedData.length === 0);
      } else {
        setNoData(true);
        setLedgerData([]);

        // "01" is just "no matches": the empty table says so
        if (response?.data?.respCode !== "01" && response?.data?.data !== null) {
          CustomAlert({
            title: "No Data",
            text: "No data available for the specified criteria.",
            icon: "info",
            confirmButtonText: "Okay",
          });
        }
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      }).then((result) => {
        if (result.isConfirmed) {
          sessionStorage.clear();
          window.location.href = "/";
        }
      });
    } finally {
      setLoading(false);
    }
  };

const handleDownloadExcel = () => {
  const requestBody = {
    username: loggedInUserName,
    ...filterData,
    fromDate: formatDate(filterData?.fromDate),
    toDate: formatDate(filterData?.toDate),
  };

  exportAllData({
    apiUrl: apiList.FETCH_CUSTOMER_ENQUIRIES,
    requestBody,
    headers: CustomerHeader,
    headerToKeyMap: CustomerHeaderToKeyMap,
    fileName: "CustomerEnquiries.xlsx",
  });
};

  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <div>
      <HeaderWithBreadcrumb title="Customer Enquiries" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchUserData(1, next, pageSize);
        }}
        search={{ key: "search", placeholder: "Search name, company, mobile, email, city or pincode" }}
        selects={[{ key: "type", allLabel: "All types", chipLabel: "Type", options: ENQUIRY_TYPE_OPTIONS }]}
        dateRange={{ fromKey: "fromDate", toKey: "toDate", label: "Received" }}
      />
      <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
        {loading ? (
          <Loader />
        ) : (
          <>
            <MasterTable
              headers={CustomerHeader}
              data={ledgerData}
              filterFields={["type"]}
              entityType="CustomerEnquiries"
              headerToKeyMap={CustomerHeaderToKeyMap}
              loading={loading}
              noData={noData}
              pageDetails={pageDetails}
              currentPage={currentPage}
              totalRecord={totalRecord}
              handleReset={handleReset}
              setCurrentPage={setCurrentPage}
              // fetchNextData={(pageNo) => fetchUserData(pageNo, filterData)}
              fetchNextData={(pageNo, size) => fetchUserData(pageNo, filterData, size)}
              filterData={filterData}
              openModalFilter={openModalFilter}
              pageSize={pageSize}
              // pageSize={pageSizeCount}
              setPageSize={setPageSize}
              fetchData={() => fetchUserData(1, defaultFilterData, pageSize)}
              // fetchData={() => fetchUserData(1, filterData, pageSize)}
              handleExport={handleDownloadExcel}
              exportLoading={exportLoading}
              hideFilterControls
            />
            {isModalOpenFilter && (
              <CustomerFilterModal
                show={isModalOpenFilter}
                handleClose={closeModalFilter}
                setFilterData={setFilterData}
                filterData={filterData}
                handleReset={handleReset}
                setLedgerData={setLedgerData}
                setPageDetails={setPageDetails}
                setNoData={setNoData}
                currentPage={currentPage}
                // pageSize={pageSizeCount}
                pageSize={pageSize}
                setCurrentPage={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerEnquiries;
